import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, TextField } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEventListener from '../../hooks/useEventListener.hooks';
import useIssueFlagWindow from '../../hooks/issueReport/useIssueFlagWindow.hooks';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { ISSUE_DETAIL_CHAR_CAP } from '../../redux/features/issueReport/issueReport.slice';
import Colors from '../../utils/color';
import { focusOmniBar } from '../../utils/omniBarFocus';
import {
  HITL3_FONT_SIZE,
  HITL3_TITLE_FONT_SIZE,
} from '../../utils/hitl3Typography';
import FlagWindowDial from './FlagWindowDial';
import IssueTypeChips from './IssueTypeChips';

/**
 * HITL-1501: confirmation that a flag landed, the Undo window, and the ONLY chance to
 * classify it.
 *
 * There is no header chip, no modal and no task-router surface — a report not
 * classified here stays `Unclassified` permanently. That is why the window is 10s
 * rather than 5s, why the detail field pauses the clock, and why the whole taxonomy
 * is on screen at once instead of behind a drill-in.
 *
 * ## Not a FloatingBanner
 *
 * That component is a one-line flex row in a fixed slot shared with RefreshBanner and
 * TaskRouterOnBanner. Growing it into a panel for one caller would drift the other
 * two, so this owns its own geometry — while deliberately keeping the family's slot
 * (`top: 100px`, centred, daintree), because that is where the agent's eye already
 * goes for a screen-level notice.
 *
 * ## Non-modal by construction
 *
 * Absolutely positioned, no MUI Modal, no backdrop, no focus trap, and nothing is
 * autofocused. No ordering control is ever blocked or delayed.
 *
 * `data-issue-report="true"` sits on the ROOT and that is load-bearing: the
 * agent-interception guards listen on `document` in the CAPTURE phase and match with
 * `target.closest()`, so the marker must be an ANCESTOR of the real event target —
 * a chip's inner text node, the icon svg, the input. Marking anything deeper would
 * pop the "would you like to take over?" dialog on every classification click and
 * every keystroke in the detail field.
 *
 * Undo emits a RETRACTION at the next revision; it does not cancel the revision-0
 * send. Committing on press is the point — a refresh, a car exit or a network blip
 * inside the window would otherwise lose the flag, and blips are exactly what agents
 * flag.
 */
const IssueFlagPanel = () => {
  const {
    commitIssueType,
    voidIssue,
    pauseFlagWindow,
    resumeFlagWindow,
    closeFlagWindow,
  } = useIssueReport();
  const flagWindow = useIssueFlagWindow();
  const [detail, setDetail] = useState('');
  const detailRef = useRef<HTMLInputElement>(null);

  // The detail the agent typed must survive the unmount that closing causes, so the
  // timeout commit reads a ref rather than state torn down mid-effect.
  const detailValueRef = useRef('');
  detailValueRef.current = detail;
  // The clock is paused ONCE, on the first keystroke — not on focus, and not per
  // keystroke. Focus cannot be the trigger any more: the field is focused
  // automatically when the panel opens, so pausing on focus would mean the countdown
  // never ran and every panel lived to the 60s hard cap. Per-keystroke would be a
  // second clock racing the first, and the dial would stutter as the agent thinks.
  const pausedRef = useRef(false);

  const issueId = flagWindow?.issueId ?? null;
  // The Escape listener is registered unconditionally (hooks cannot be conditional),
  // so it reads the live id from a ref rather than closing over a stale one.
  const flagWindowRef = useRef<string | null>(null);
  flagWindowRef.current = issueId;
  // Set once a chip has already committed this window's detail, so the close
  // transition below does not send it a second time at the next revision.
  const committedRef = useRef(false);

  /**
   * One window, one detail. Resetting per window id rather than on unmount, because
   * this component never unmounts — it renders null between flags, so without this
   * the next flag would open showing the previous agent's text.
   *
   * The cleanup is also where a TIMED-OUT detail gets recorded: the agent typed
   * something and the clock ran out before they picked a chip. Discarding it would
   * throw away the only signal that says what the taxonomy is missing.
   */
  useEffect(() => {
    setDetail('');
    committedRef.current = false;
    pausedRef.current = false;
    if (!issueId) return undefined;
    // Focused explicitly rather than via the `autoFocus` prop: the omnibar refocuses
    // itself on every render, so the caret has to be claimed after that has settled.
    // `focusOmniBarInput` then holds it here, because the panel is marked
    // data-issue-report.
    const focusTimer = setTimeout(() => detailRef.current?.focus(), 0);
    return () => {
      clearTimeout(focusTimer);
      // Hand the caret straight back, so the agent can type the order the instant
      // the panel goes. Without this it sits on <body> until some unrelated
      // re-render happens to run the omnibar's own refocus.
      //
      // Deferred a tick, symmetrically with how it is claimed above: the panel's DOM
      // is torn down around this cleanup, and focusing while the currently-focused
      // node is being removed leaves the caret on <body> anyway.
      setTimeout(focusOmniBar, 0);
      const typed = detailValueRef.current.trim();
      if (!typed || committedRef.current) return;
      commitIssueType({
        issue_id: issueId,
        issue_type: null,
        issue_detail: typed,
      });
    };
    // commitIssueType is stable across renders (useCallback in useIssueReport).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  /**
   * Esc is TWO-STAGE. With focus in the detail field it blurs and resumes the clock,
   * leaving the panel up; only a second press closes. A single Esc that destroyed a
   * half-typed detail would be the wrong default next to the ordering controls.
   *
   * WINDOW CAPTURE, not a React onKeyDown on the panel. A React handler only fires
   * when focus is already inside the panel, and it almost never is: the omnibar
   * refocuses itself on every render, so Escape would be delivered there and the
   * panel could never be dismissed by keyboard. Window capture also runs before
   * Autocomplete's own `clearOnEscape`, so dismissing the panel cannot wipe a
   * half-typed order.
   */
  const handleEscape = (event: Event) => {
    const e = event as KeyboardEvent;
    if (e.key !== 'Escape' || !flagWindowRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    if (document.activeElement === detailRef.current) {
      detailRef.current?.blur();
      return;
    }
    pausedRef.current = false;
    // Closing with text typed still records it — see the timeout path in
    // useIssueFlagWindow. A typed detail is an explicit agent action.
    // The close transition records a typed detail, so this only has to close.
    closeFlagWindow();
  };

  useEventListener('keydown', handleEscape, window, true);


  /**
   * Every chip behaves identically — commit and close, detail optional. There is no
   * blocking branch anywhere, including for `Not listed`: a confirm step buys nothing
   * when re-classifying is just another revision.
   */
  const handleSelect = useCallback(
    (issueType: string) => {
      const id = flagWindowRef.current;
      if (!id) return;
      committedRef.current = true;
      commitIssueType({
        issue_id: id,
        issue_type: issueType,
        issue_detail: detailValueRef.current.trim() || null,
      });
      // The thunk deliberately does not close the window — it is also used by the
      // timeout path, where the window has already gone. Closing is the caller's job,
      // and a chip click must close IMMEDIATELY rather than wait out the countdown.
      closeFlagWindow();
    },
    [commitIssueType, closeFlagWindow]
  );

  const handleDetailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!pausedRef.current) {
        pausedRef.current = true;
        pauseFlagWindow();
      }
      setDetail(e.target.value);
    },
    [pauseFlagWindow]
  );

  const handleDetailBlur = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    resumeFlagWindow();
  }, [resumeFlagWindow]);

  const handleUndo = useCallback(() => {
    const id = flagWindowRef.current;
    if (!id) return;
    // Undo retracts the whole report, so a typed detail must not be re-sent by the
    // close transition afterwards.
    committedRef.current = true;
    voidIssue(id);
  }, [voidIssue]);


  // AFTER every hook — an early return above any of them changes the hook count
  // between renders, which React rejects outright.
  if (!flagWindow) return null;

  return (
    <Box
      data-issue-report="true"
      data-testid="issue-flag-panel"
      role="region"
      aria-label="Issue flagged for this order"
      sx={{
        position: 'absolute',
        top: '100px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(760px, calc(100% - 32px))',
        backgroundColor: Colors.daintree,
        color: Colors.white,
        borderLeft: `4px solid ${Colors.green}`,
        p: 2,
        zIndex: 9999,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: HITL3_TITLE_FONT_SIZE,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: Colors.green }} />
            Issue flagged for this order
          </Box>

          {/* Directly under the title, ABOVE the chips: with one global escape hatch
              this is the only thing that says WHAT the taxonomy missed, and the flow
              reads top-to-bottom — type the context, then click the type that
              sends it. */}
          <TextField
            inputRef={detailRef}
            value={detail}
            onChange={handleDetailChange}
            // Typing PAUSES the countdown; blur resumes from the banked remainder.
            // Not "typing extends": extension has no exit when an agent is pulled
            // into an order mid-sentence.
            onBlur={handleDetailBlur}
            placeholder="Add a few more details (optional)"
            fullWidth
            size="small"
            inputProps={{ maxLength: ISSUE_DETAIL_CHAR_CAP }}
            sx={{
              mt: 1.25,
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: Colors.white,
                fontSize: HITL3_FONT_SIZE,
              },
              '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
              // A clear focused border: this field now takes the caret on open, so
              // the agent must be able to see at a glance where their keystrokes are
              // going — the omnibar is inches away.
              '& .Mui-focused fieldset': {
                borderColor: `${Colors.green} !important`,
                borderWidth: '2px !important',
              },
            }}
          />
        </Box>

        <FlagWindowDial
          expiresAt={flagWindow.expiresAt}
          remainingMs={flagWindow.remainingMs}
          isPaused={flagWindow.isPaused}
          onUndo={handleUndo}
        />
      </Box>

      {/* No height cap and no scroll: all 19 chips are meant to be readable in one
          glance, and a scrollbar would hide part of the taxonomy behind a gesture —
          which is the exact failure (a value buried below the fold) this panel
          exists to fix. It measured well inside the smallest supported viewport. */}
      <Box sx={{ mt: 1.5 }}>
        <IssueTypeChips onSelect={handleSelect} />
      </Box>
    </Box>
  );
};

export default IssueFlagPanel;
